import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, Switch, Button, Row, Col,
  message, Space, Divider, Tabs, Typography, Badge, Grid
} from 'antd';
import { SaveOutlined, EditOutlined, AimOutlined } from '@ant-design/icons';
import BallPlacer from './BallPlacer';
import { getGameTypes } from '../../api/services/gameTypeService';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * ExerciseEditor Component
 * Modal for creating/editing practice exercises with tabbed layout.
 * Responsive design for mobile and desktop.
 */
const ExerciseEditor = ({
  visible,
  onClose,
  onSave,
  exercise = null, // null = create new, object = edit existing
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [balls, setBalls] = useState([]);
  const [gameTypes, setGameTypes] = useState([]);
  const [loadingGameTypes, setLoadingGameTypes] = useState(false);
  const [tableSize, setTableSize] = useState('9ft');
  const [ballSet, setBallSet] = useState('universal');
  const [cueHitPoint, setCueHitPoint] = useState(null);
  const [activeTab, setActiveTab] = useState('description');

  // Responsive breakpoints
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Load game types
  useEffect(() => {
    const loadGameTypes = async () => {
      setLoadingGameTypes(true);
      try {
        const data = await getGameTypes();
        setGameTypes(data);
      } catch (error) {
        console.error('Failed to load game types:', error);
      } finally {
        setLoadingGameTypes(false);
      }
    };
    loadGameTypes();
  }, []);

  // Initialize form when exercise changes
  useEffect(() => {
    if (exercise) {
      form.setFieldsValue({
        name: exercise.name,
        beschreibung: exercise.beschreibung || '',
        aufbau: exercise.aufbau || '',
        aufgabe: exercise.aufgabe,
        wertung: exercise.wertung || '',
        difficulty: exercise.difficulty,
        game_type: exercise.game_type,
        tags: exercise.tags || '',
        is_public: exercise.is_public || false,
      });
      setBalls(exercise.baelle || []);
    } else {
      form.resetFields();
      setBalls([]);
    }
    setActiveTab('description');
  }, [exercise, form, visible]);

  // Handle form submission
  const handleSubmit = async (values) => {
    if (balls.length === 0) {
      message.warning('Bitte platziere mindestens einen Ball auf dem Tisch.');
      setActiveTab('balls');
      return;
    }

    const exerciseData = {
      ...values,
      baelle: balls,
    };

    try {
      await onSave(exerciseData);
      message.success(exercise ? 'Übung aktualisiert!' : 'Übung erstellt!');
      handleClose();
    } catch (error) {
      message.error('Fehler beim Speichern der Übung.');
      console.error('Save exercise error:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setBalls([]);
    setActiveTab('description');
    onClose();
  };

  // Calculate responsive scale for table
  const getTableScale = () => {
    if (!screens.sm) return 0.28; // Extra small (phones)
    if (!screens.md) return 0.32; // Small tablets
    if (!screens.lg) return 0.36; // Tablets
    return 0.40; // Desktop
  };

  // Tab items
  const tabItems = [
    {
      key: 'description',
      label: (
        <span>
          <EditOutlined /> {isMobile ? 'Info' : 'Beschreibung'}
        </span>
      ),
      children: (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Form.Item
            name="name"
            label="Name der Übung"
            rules={[{ required: true, message: 'Bitte Namen eingeben' }]}
          >
            <Input placeholder="z.B. Positionsspiel auf die 9" size={isMobile ? 'middle' : 'large'} />
          </Form.Item>

          <Form.Item
            name="aufgabe"
            label="Aufgabe"
            rules={[{ required: true, message: 'Bitte Aufgabe beschreiben' }]}
          >
            <TextArea
              rows={isMobile ? 3 : 4}
              placeholder="Was soll der Spieler erreichen?"
            />
          </Form.Item>

          <Form.Item
            name="beschreibung"
            label="Beschreibung"
          >
            <TextArea
              rows={isMobile ? 2 : 3}
              placeholder="Allgemeine Beschreibung der Übung"
            />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="game_type"
                label="Spieltyp"
                rules={[{ required: true, message: 'Bitte Spieltyp wählen' }]}
              >
                <Select
                  placeholder="Spieltyp wählen"
                  loading={loadingGameTypes}
                  size={isMobile ? 'middle' : 'large'}
                >
                  {gameTypes.map(gt => (
                    <Option key={gt.id} value={gt.id}>{gt.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="difficulty"
                label="Schwierigkeit"
                rules={[{ required: true }]}
              >
                <Select size={isMobile ? 'middle' : 'large'}>
                  <Option value={1}>Leicht</Option>
                  <Option value={2}>Mittel</Option>
                  <Option value={3}>Schwer</Option>
                  <Option value={4}>Experte</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="aufbau"
            label="Aufbau-Anleitung"
          >
            <TextArea
              rows={2}
              placeholder="Wie soll der Tisch aufgebaut werden?"
            />
          </Form.Item>

          <Form.Item
            name="wertung"
            label="Wertung / Bewertungskriterien"
          >
            <TextArea
              rows={2}
              placeholder="Wie wird der Erfolg gemessen?"
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags (kommagetrennt)"
          >
            <Input placeholder="z.B. Position, Draw, 9-Ball" />
          </Form.Item>

          <Divider style={{ margin: isMobile ? '12px 0' : '24px 0' }} />

          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 16 : 0,
          }}>
            <Form.Item
              name="is_public"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Space>
                <Switch />
                <Text>Öffentlich teilen</Text>
              </Space>
            </Form.Item>
            <Button
              type="primary"
              size={isMobile ? 'middle' : 'large'}
              onClick={() => setActiveTab('balls')}
              block={isMobile}
            >
              Weiter zum Ball-Aufbau
            </Button>
          </div>
        </div>
      ),
    },
    {
      key: 'balls',
      label: (
        <span>
          <AimOutlined /> {isMobile ? 'Bälle' : 'Ball-Aufbau'}
          {balls.length > 0 && (
            <Badge
              count={balls.length}
              size="small"
              style={{ marginLeft: 8, backgroundColor: '#52c41a' }}
            />
          )}
        </span>
      ),
      children: (
        <div>
          <BallPlacer
            balls={balls}
            onBallsChange={setBalls}
            tableSize={tableSize}
            ballSet={ballSet}
            onTableSizeChange={setTableSize}
            onBallSetChange={setBallSet}
            scale={getTableScale()}
            cueHitPoint={cueHitPoint}
            onCueHitPointChange={setCueHitPoint}
            compact={isMobile}
          />

          <Divider style={{ margin: isMobile ? '12px 0' : '24px 0' }} />

          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column-reverse' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 12 : 0,
          }}>
            <Button
              onClick={() => setActiveTab('description')}
              block={isMobile}
            >
              Zurück zur Beschreibung
            </Button>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: isMobile ? 8 : 12,
            }}>
              {!isMobile && (
                <Text type="secondary">
                  {balls.length} {balls.length === 1 ? 'Ball' : 'Bälle'} platziert
                </Text>
              )}
              <Button
                type="primary"
                size={isMobile ? 'large' : 'large'}
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                disabled={balls.length === 0}
                block={isMobile}
              >
                {exercise ? 'Übung speichern' : 'Übung erstellen'}
                {isMobile && balls.length > 0 && ` (${balls.length})`}
              </Button>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={exercise ? 'Übung bearbeiten' : 'Neue Übung erstellen'}
      open={visible}
      onCancel={handleClose}
      width={isMobile ? '100%' : 1000}
      footer={null}
      destroyOnClose
      styles={{
        body: {
          minHeight: isMobile ? 'auto' : 500,
          padding: isMobile ? '12px' : '24px',
        }
      }}
      style={isMobile ? { top: 20, maxWidth: '100%', margin: '0 8px' } : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          difficulty: 2,
          is_public: false,
        }}
        size={isMobile ? 'middle' : 'large'}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size={isMobile ? 'small' : 'large'}
          style={{ marginTop: -8 }}
        />
      </Form>
    </Modal>
  );
};

export default ExerciseEditor;
